import styled, { css } from 'styled-components'
import { pxToRem, getButtonColor, getButtonRadius, getButtonTextColor } from 'styles/functions'

interface StyleButtonFlatProps {
  theme: any;
  $themeCustom: any;
}
const styleButtonFlat = css<StyleButtonFlatProps>`
  width: 66%;
  margin: auto;
  padding: 2px 6px;
  font-size: 12px;
  line-height: 15px;
    
  border-radius: ${({ theme, $themeCustom }) => pxToRem($themeCustom ? $themeCustom.details.buttons.radius : theme.button_radius/1.5 || 0)};
`;

interface StyledButtonContainerProps {
  theme: any;
  $noMargin: boolean
}
export const StyledButtonContainer = styled.div<StyledButtonContainerProps>`
  display: flex;
  align-items: ${({ theme }) => (theme.button_centered ? 'center' : 'flex-start')};
  margin: ${({ $noMargin }) => ($noMargin ? '1px' : `${pxToRem(20)}`)};
  flex-flow: column;
`

interface StyledButtonProps {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
  $themeCustom: any;
}

export const StyledButton = styled.button<StyledButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  text-align: center;
  word-break: break-all;

  ${({ theme }) =>
    theme.button_background_url &&
    css`
      background: url(${theme.button_background_url});
    `};

  background-color: ${({ theme, $isPhotobooth, $themeCustom }) =>
    getButtonColor(theme, $themeCustom, $isPhotobooth)};
  color: ${({ theme, $isPhotobooth }) => getButtonTextColor(theme, $isPhotobooth)};
  padding: ${({ theme }) => (theme.button_padding ? theme.button_padding : pxToRem(12))};
  width: ${({ theme }) => (theme.button_width ? pxToRem(theme.button_width) : '100%')};
  border: ${({ theme, $themeCustom }) =>
    theme.button_no_border
      ? 'none'
      : `2px solid ${($themeCustom && $themeCustom.details.buttons.color) || theme.framed_background || theme.button_color || '#34b076'}`};

  ${({ theme, $isPhotobooth, $themeCustom }) =>
    !theme.button_no_border &&
    css`
      border: ${$themeCustom ? $themeCustom.details?.buttons?.borderThickness : '2px'} solid
        ${theme.button_border_color || getButtonColor(theme, $themeCustom, $isPhotobooth)};
    `}

  border-radius: ${({ theme, $themeCustom }) => getButtonRadius(theme, $themeCustom)};
  box-shadow: ${({ theme }) => theme.button_shadow || 'none'};
  transition: all 0.2s;
  transition-timing-function: cubic-bezier(0.25, 0.1, 0.25, 0.1);
  cursor: pointer;

  &.disabled {
    color: white;
    pointer-events: none;
    background-color: lightgrey;
    border-color: lightgrey;
    cursor: not-allowed;
  }

  label {
    display: flex;
    align-items: center;
    cursor: pointer;
    pointer-events: none;
    ${({ theme }) =>
      theme.button_text_size &&
      css`
        font-size: ${pxToRem(theme.button_text_size)};
      `};
    ${({ theme }) =>
      theme.button_font &&
      css`
        font-family: ${theme.button_font};
      `};
    ${({ theme }) =>
      theme.button_text_uppercase &&
      css`
        text-transform: uppercase;
      `};
    ${({ theme }) =>
      theme.button_text_weight &&
      css`
        font-weight: ${theme.button_text_weight};
      `};
    ${({ theme }) =>
      theme.button_text_lineheight &&
      css`
        line-height: ${pxToRem(theme.button_text_lineheight)};
      `};
    ${({ theme }) =>
      theme.button_text_letter_spacing &&
      css`
        letter-spacing: ${pxToRem(theme.button_text_letter_spacing)};
      `};
  }

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme }) => (theme.button_hover_color ? theme.button_hover_color : 'transparent')};

    color: ${({ theme, $isPhotobooth, $themeCustom }) =>
      theme.button_hover_text_color ||
      theme.button_text_color ||
      getButtonColor(theme, $themeCustom, $isPhotobooth)};

    ${({ theme }) =>
      (theme.button_hover_color || theme.button_hover_border_color) &&
      css`
        border-color: ${({ theme }) =>
          theme.button_hover_border_color ? theme.button_hover_border_color : theme.button_border_color};
      `}
  }

  ${({ $flat, $themeCustom }) =>
    $flat &&
    css`
      ${styleButtonFlat}
    `};

  ${({ theme }) => theme.custom_button && theme.custom_button}
`

interface StyledButtonOutlinedProps {
  theme: any;
  $isPhotobooth: boolean;
  $flat: boolean;
}
export const StyledButtonOutlined = styled(StyledButton)<StyledButtonOutlinedProps>`
  background-color: ${({ theme }) => theme.button_outlined_color || 'transparent'};
  color: ${({ theme, $isPhotobooth, $themeCustom }) =>
    theme.button_outlined_text_color || getButtonColor(theme, $themeCustom, $isPhotobooth)};
  border: ${({ theme, $themeCustom }) =>
    `2px solid ${($themeCustom && $themeCustom.details.buttons.color) || theme.framed_background || theme.button_color || '#34b076'}`};
  border-color: ${({ theme, $themeCustom }) =>
    $themeCustom && $themeCustom.details.buttons.color
      ? $themeCustom.details.buttons.color
      : theme.button_outlined_border_color || theme.button_outlined_text_color};
  word-break: break-all;

  &:hover:not(.disabled, :focus) {
    background-color: ${({ theme, $isPhotobooth, $themeCustom }) =>
      theme.button_outlined_hover_color || getButtonColor(theme, $themeCustom, $isPhotobooth)};
    color: ${({ theme }) => theme.button_outlined_hover_text_color || 'white'};

    border-color: ${({ theme, $isPhotobooth, $themeCustom }) =>
      $themeCustom && $themeCustom.details.buttons.color
        ? $themeCustom.details.buttons.color
        : theme.button_outlined_hover_border_color
          ? theme.button_outlined_hover_border_color
          : theme.button_outlined_hover_color || getButtonColor(theme, false, $isPhotobooth)};
  }

  ${({ $flat, $themeCustom }) =>
    $flat &&
    css`
      ${styleButtonFlat}
    `};

  ${({ theme }) => theme.custom_alternate_button && theme.custom_alternate_button}
`
